import React, { useEffect, useMemo, useState } from 'react';
import Appstore from '@/pages/opinion/assets/svg/channel/app_store.svg';
import Metacritic from '@/pages/opinion/assets/svg/channel/metacritic.svg';
import Googleplay from '@/pages/opinion/assets/svg/channel/google_play.svg';
import AppAnnie from '@/pages/opinion/assets/svg/app-annie.svg';
import { arrayToObj, dealwithNewOldData } from '@/pages/opinion/utils';
import { getCompetitorKeyMetricsList } from '@/pages/opinion/services/competitor';
import {
  percentageFormat,
  reviewsFormat,
  scoreFormat,
  sentimentFormat,
} from '@/pages/opinion/utils/value';
import RingRatioBubble from '@/pages/opinion/components/Snippets/RingRatioBubble';
import PercentageItem from '@/pages/opinion/components/Snippets/PercentageItem';
import { formatDateFunc } from '@/pages/opinion/utils/time';
import CardCommon from '@/pages/opinion/components/CardCommon';
import { useIntl } from 'umi';
import {
  excludeChannels,
  hasExcludeChannelsGameId,
} from '@/pages/opinion/constants';
import BubbleCommon, {
  gameListMentionsTips,
} from '@/pages/opinion/components/BubbleCommon';
import { mergeExcludeChannel } from '@op/components/FilterCommon/utils';

// KeyMetricsRow模块的每一行
export default function KeyMetricsRow({
  isShadow,
  filter,
  games,
  isCache,
}: {
  isShadow: boolean;
  isCache: boolean;
  filter: any;
  games: Array<any>;
}) {
  const [loading, setLoading] = useState(false);
  // 表格数据
  const [keyMetricsInfo, setKeyMetricsInfo] = useState<any>({});

  const getCompetitorKeyMetrics = async () => {
    setLoading(true);
    let data = await getCompetitorKeyMetricsList(
      mergeExcludeChannel(
        {
          ...filter,
          games,
          need_link: true,
          global_except_china: false,
          metrics: [
            'mentions',
            'avg_sentiment',
            'engagement',
            'sentiment',
            'account_posts',
            'user_score',
            'avg_dau',
            'lifetime_score',
            'selected_score',
          ],
        },
        excludeChannels,
      ),
    );
    setLoading(false);
    if (data) {
      const dataMap = arrayToObj(
        data,
        (item) => item?.game?.edition_unified_id,
      );
      let keyMetricsInfo = games.reduce(
        (preEle, { edition_unified_id }: any) => {
          let { value } = dataMap[edition_unified_id] || { value: {} };
          let { new_list, old_list, link_time } = value || {};
          let { new_start_date, new_end_date, old_start_date, old_end_date } =
            link_time || {};
          const newTime = [new_start_date, new_end_date];
          const oldTime = [old_start_date, old_end_date];

          let result = dealwithNewOldData(
            new_list,
            old_list,
            'key',
            ['value'],
            (newItem, oldItem: any) => {
              return {
                pre_value: oldItem?.value || 0,
                metric_name: newItem.key,
                newTime,
                oldTime,
              };
            },
            undefined,
            undefined,
            () => 'dod',
          );

          return { ...preEle, [edition_unified_id]: arrayToObj(result, 'key') };
        },
        {},
      );
      setKeyMetricsInfo(keyMetricsInfo);
    } else {
      setKeyMetricsInfo({});
    }
  };

  useEffect(() => {
    if (games.length > 0 && filter && !isCache) {
      getCompetitorKeyMetrics();
    }
  }, [games, isCache]);

  const formateDate = formatDateFunc(filter.date_type);

  // 指标列
  const KeyMetricsColumns = useMemo(() => {
    let options = [
      {
        valueKey: 'mentions',
        title: 'Mentions',
        valueFormat: reviewsFormat,
        Component: CommonTableRowItem,
        dateFormat: formateDate,
      },
      {
        valueKey: 'account_posts',
        title: 'Account Posts',
        valueFormat: reviewsFormat,
        Component: CommonTableRowItem,
        dateFormat: formateDate,
      },
      {
        valueKey: 'engagement',
        title: 'Engagement',
        valueFormat: reviewsFormat,
        Component: CommonTableRowItem,
        dateFormat: formateDate,
      },
      {
        valueKey: 'avg_sentiment',
        title: 'Sentiment',
        valueFormat: sentimentFormat,
        Component: CommonTableRowItem,
        dateFormat: formateDate,
      },
      {
        valueKey: 'non_negative_ratio',
        title: 'Non Negative%',
        valueFormat: (value: number) => percentageFormat(value * 100, '-', 2),
        Component: CommonTableRowItem,
        dateFormat: formateDate,
      },
      {
        valueKey: 'selected_score',
        title: 'Selected User Score',
        keyMap: {
          mobile: ['googleplay_selected_score', 'appstore_selected_score'],
          pc: [],
          console: [],
        },
        valueFormat: scoreFormat,
        Component: GameScoreTableRowItem,
        dateFormat: formateDate,
      },
      {
        valueKey: 'lifetime_score',
        title: 'Lifetime User Score',
        valueFormat: scoreFormat,
        Component: GameScoreTableRowItem,
        dateFormat: formateDate,
        keyMap: {
          mobile: ['appstore_lifetime_score', 'googleplay_lifetime_score'],
          pc: ['metacritic_lifetime_score'],
          console: ['metacritic_lifetime_score'],
        },
      },
      {
        valueKey: 'avg_dau',
        title: 'AVG DAU',
        valueFormat: reviewsFormat,
        Component: CommonTableRowItem,
        dateFormat: formateDate,
      },
    ];
    const flag = games.every((item) => item?.entity_type !== 'mobile');
    // 游戏全为pc/console 不显示 selected user score
    if (flag) {
      options.splice(5, 1);
    }
    // 时间颗粒为hour时 不显示AVG   游戏全为pc/console 不显示AVG
    if (filter.date_type === 'hourly' || flag) {
      options.splice(-1, 1);
    }

    return options;
  }, [filter, games]);

  // 高亮指标对象
  const lightMetricsMap = useMemo(() => {
    // 只有一个游戏不需要高亮 直接结束
    if (games.length === 1) {
      return {};
    }

    let keys = [
      'account_posts',
      'avg_dau',
      'avg_sentiment',
      'engagement',
      'mentions',
      'negative_mentions',
      'neutral_mentions',
      'non_negative_ratio',
      'positive_mentions',
      [
        'lifetime_score',
        'appstore_lifetime_score',
        'googleplay_lifetime_score',
      ],
      [
        'selected_score',
        'googleplay_selected_score',
        'appstore_selected_score',
      ],
      'metacritic_lifetime_score',
    ];

    let entity_type = '';
    let flag = games.every((item) => {
      if (entity_type === '') {
        entity_type = item.entity_type;
      }
      return item.entity_type === entity_type;
    });
    // 游戏种类不同时 UserScore去除高亮
    if (!flag) {
      keys.splice(9, 3);
    }

    let data: any = {};
    for (const gameId in keyMetricsInfo) {
      const element = keyMetricsInfo[gameId];

      keys.forEach((key) => {
        const keyIsStr = typeof key === 'string';
        let newKey = keyIsStr ? key : (key[0] as string);
        if (!data[newKey]) {
          data[newKey] = [];
        }

        if (keyIsStr) {
          data[newKey].push({ value: element[newKey]?.value || 0, gameId });
        } else {
          // 当用户评分比较时 取平均值
          let total = key
            .slice(1)
            .reduce(
              (cPre, cCur) =>
                (element[cCur]?.value || 0) + (element[cPre]?.value || 0),
              0,
            );
          data[newKey].push({ value: total / 2 || 0, gameId });
        }
      }, data);
    }

    let result: any = {};
    for (const key in data) {
      const ele = data[key];
      ele.sort((a: any, b: any) => b.value - a.value);
      if (!result[key]) {
        result[key] = {};
      }
      let colorList = ['#DCE9FD', '#F1F7FE'];
      ele.forEach(({ gameId, value }: any, index: number) => {
        // 第一第二都为0时取消高亮,当第二大为0时 取消高亮
        if (value !== 0) {
          if (games.length === 2 && index === 1) {
            return;
          }
          // 大于等于三个游戏显示第二高亮
          result[key][gameId] = colorList[index];
        }
      });
    }
    return result;
  }, [games, keyMetricsInfo]);

  return (
    <CardCommon contentCard loading={loading} loadingHeight={300}>
      <>
        {KeyMetricsColumns.map(({ Component, ...otherProps }) => (
          <Component
            key={otherProps.valueKey}
            games={games}
            isShadow={isShadow}
            lightMetricsMap={lightMetricsMap}
            dataInfo={keyMetricsInfo}
            {...otherProps}
          ></Component>
        ))}
      </>
    </CardCommon>
  );
}
const keyMapImg: any = {
  googleplay_selected_score: Googleplay,
  appstore_selected_score: Appstore,
  appstore_lifetime_score: Appstore,
  googleplay_lifetime_score: Googleplay,
  metacritic_lifetime_score: Metacritic,
};

function GameScoreTableRowItem({
  isShadow,
  games,
  dataInfo,
  title,
  keyMap,
  valueFormat,
  dateFormat,
  lightMetricsMap,
  valueKey,
}: any) {
  const { formatMessage } = useIntl();
  return (
    <div className="table-context-item">
      <div className="first-col table-item text-name">
        {formatMessage({ id: title })}
      </div>
      {games.map(({ edition_unified_id, entity_type }: any, i: any) => {
        let keyList = keyMap[entity_type];
        return (
          <div
            key={edition_unified_id}
            className={`table-type1 col-padding ${
              i == 0 && isShadow ? 'sticky-text' : ''
            }`}
            style={{
              background: lightMetricsMap?.[valueKey]?.[edition_unified_id],
            }}
          >
            {keyList?.length ? (
              keyList.map((key: string) => {
                const { dod, newTime, oldTime, pre_value, value } =
                  dataInfo?.[edition_unified_id]?.[key] || {};
                return (
                  <div className="table-flex dbo-numtrends" key={key}>
                    <div className="row-left">
                      <img src={keyMapImg[key]} className="left-img" />
                      {valueFormat(value)}
                    </div>
                    <div className="row-right">
                      <RingRatioBubble
                        oldTime={oldTime}
                        newTime={newTime}
                        oldValue={pre_value}
                        newValue={value}
                        title={formatMessage({ id: title })}
                        formateDate={dateFormat}
                        formateValue={valueFormat}
                      >
                        <PercentageItem percentage={dod}></PercentageItem>
                      </RingRatioBubble>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="no-data-style">
                {formatMessage({ id: 'No data' })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CommonTableRowItem({
  isShadow,
  games,
  dataInfo,
  title,
  valueKey,
  valueFormat,
  dateFormat,
  lightMetricsMap,
}: any) {
  const { formatMessage } = useIntl();

  const localHasExcludeChannelsGameId = hasExcludeChannelsGameId(
    games
      ?.filter(Boolean)
      ?.map((item: { edition_unified_id: string }) => item.edition_unified_id),
  );

  return (
    <div className="table-context-item">
      <div className="first-col table-item text-name">
        {valueKey === 'avg_dau' ? (
          <div className="text-name-flex">
            {formatMessage({ id: title })}
            <img src={AppAnnie} className="tip-icon" />
          </div>
        ) : valueKey === 'mentions' ? (
          <>
            <span>{formatMessage({ id: title })}</span>
            {localHasExcludeChannelsGameId && (
              <BubbleCommon
                placement="top"
                tipsMap={gameListMentionsTips}
                iconStyle={{ position: 'relative', top: 3, marginLeft: 5 }}
              ></BubbleCommon>
            )}
          </>
        ) : (
          formatMessage({ id: title })
        )}
      </div>
      {games?.map(({ edition_unified_id }: any, i: any) => {
        const { dod, newTime, oldTime, pre_value, value } =
          dataInfo?.[edition_unified_id]?.[valueKey] || {};
        return (
          <div
            key={edition_unified_id}
            className={`table-type1 col-padding ${
              i == 0 && isShadow ? 'sticky-text' : ''
            }`}
            style={{
              background: lightMetricsMap?.[valueKey]?.[edition_unified_id],
            }}
          >
            <div className="table-flex dbo-numtrends">
              <div className="row-left">{valueFormat(value)}</div>
              <div className="row-right">
                <RingRatioBubble
                  oldTime={oldTime}
                  newTime={newTime}
                  oldValue={pre_value}
                  newValue={value}
                  title={formatMessage({ id: title })}
                  formateDate={dateFormat}
                  formateValue={valueFormat}
                >
                  <PercentageItem percentage={dod}></PercentageItem>
                </RingRatioBubble>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
